/* eslint-disable no-secrets/no-secrets */
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WINDOW } from '@ng-web-apis/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, mergeMap, of } from 'rxjs';
import { concatMap, filter, map, switchMap, take } from 'rxjs/operators';

import { SeoApiRepository } from '../api/seo.api.repository';

import {
    SetHead,
    SetHeadSuccess,
    SetCategorySeo,
    SetCheckoutSeo,
    SetDefaultSeo,
    SetErrorPageSeo,
    SetNoindex,
    SetGiftCardSeo,
    SetHomeSeo,
    SetLastSectionTranslated,
    SetMyAccountSeo,
    SetNoIndexSuccess,
    SetOrderStatus,
    SetProductSeo,
    SetShopCartSeo,
    SetStoreLocatorSeo,
    SetWorldwideSeo,
    SetLookbookSeo,
    SetCategoryNoindex,
} from './seo.actions';

import { checkNoindex } from '@core/application/use-cases/seo/check-no-index.use-case';
import { removeCountryAndLanguageFromUrl } from '@core/application/use-cases/url/remove-country-lang.use-case';
import { applicationCategoryPagesMap, applicationHomePagesMap } from '@core/domain/constants/application-pages.const';
import { ApplicationSections } from '@core/domain/models/application-section.model';
import { CoreMenuCategory } from '@core/domain/models/category.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { NoIndexConfiguration } from '@core/domain/models/seo.models';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { RouteTypeService } from '@core/infrastructure/router/route-type.service';
import { SetPage } from '@core/infrastructure/state/core-commands.actions';
import { ConfigureHomeLinkSuccess } from '@core/infrastructure/state/core-events.actions';
import {
    getCmsConfigValue,
    getCountryCode,
    getCurrencyInfo,
    getDefaultLanguage,
    getLanguageCode,
    getStoreAndLanguage,
    getStoreId,
    getStoreList,
} from '@core/infrastructure/state/core.selectors';
import { CoreStorageService } from '@core/infrastructure/storage/storage.service';
import { SeoService } from '@core/ui/services/seo.service';
import { CategoriesSelectors } from '@features/category';
import { getProductTitle } from '@features/seo/application/use-cases/get-product-title.use-case';
import { indexHomeTemplates } from '@features/seo/application/use-cases/index-home-templates.use-case';
import { capitalize, checkMatch } from '@frontend/common/utils';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { PageTitleService } from '@layout/ui/services/page-title.service';
import { StoreLanguage } from '@mdfrontw/core';
import { LoadLandingPage } from '@pages/category/store/category-page.actions';
import { LoadLastSection, NoOpAction, SetLastSection } from '@shared/menu-categories/store/menu-categories.actions';

/**
 * SEO Effects
 */
@Injectable()
export class SeoEffects {
    private readonly noindexTemplates = ['ItxBagCollectionSliderCreatorsPage', 'ItxRunwayPage', 'ItxStoriesPage'];

    private readonly getMenuCategories$ = this.store.select(CategoriesSelectors.getMenuCategories);

    private readonly getCategoriesNavigation$ = this.store.select(CategoriesSelectors.getCategoriesNavigation);

    constructor(
        private readonly actions$: Actions,
        private readonly pageTitleService: PageTitleService,
        private readonly seoService: SeoService,
        private readonly storageService: CoreStorageService,
        private readonly store: Store,
        private readonly route: ActivatedRoute,
        private readonly translateService: TranslateService,
        private readonly platform: CorePlatformService,
        private readonly seoApiRepository: SeoApiRepository,
        @Inject(WINDOW) private readonly window: CommonWindow,
        private readonly routeTypeService: RouteTypeService,
    ) {}

    public loadHomeSuccessEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ConfigureHomeLinkSuccess, LoadLastSection),
            filter(() => this.platform.isBrowser()),
            switchMap(() => this.store.select(routerPublicQueries.getDataFromRouter)),
            filter(params => !!params),
            // eslint-disable-next-line rxjs/no-unsafe-first
            take(1),
            // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
            map((params) => {
                const lastSectionSaved: string | undefined = this.storageService.getValue<string>('LAST_SECTION');
                const page = params?.pageName;
                if (checkMatch(page, applicationHomePagesMap)) {
                    const sectionQueryParam = this.route.snapshot?.queryParamMap?.get('section');
                    if (sectionQueryParam) {
                        return SetLastSection({ section: sectionQueryParam, navigation: false });
                    }

                    // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
                    let section;

                    if (page === 'ItxHomePage' || page === 'ItxHomeWomenPage') {
                        section = ApplicationSections.WOMEN;
                    }
                    if (page === 'ItxHomeMenPage') {
                        section = ApplicationSections.MEN;
                    }
                    if (page === 'ItxHomeBgPage') {
                        section = ApplicationSections.BG;
                    }

                    return SetLastSection({ section, navigation: false });
                }
                if (checkMatch(page, applicationCategoryPagesMap)) {
                    return NoOpAction();
                }
                return SetLastSection({ section: lastSectionSaved || ApplicationSections.WOMEN, navigation: false });
            })
        );
    });

    public setHead$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetHead),
            filter(() => this.platform.isBrowser()),
            map((seoData) => {
                this.pageTitleService.setTitle(seoData.title);
                const url: string = this.window.location.href;
                const urlParsed: string = seoData.isHome && !url.endsWith('/') ? `${url}/` : url; // Workaround to add end slash on homes
                this.seoService.addHeadData(
                    seoData.title,
                    seoData.metaDescription,
                    urlParsed,
                    seoData.imgSrc,
                );
            }),
            map(() => SetHeadSuccess()),
        );
    });

    // Default route changes
    public setSeoOnDefaultRouteChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetPage),
            map((seoPage: { page: string }) => {
                switch (seoPage.page) {
                    case 'ItxCategoryPage':
                    case 'ItxAngularLookbookPage':
                    case 'ItxProductPage':
                        return NoOpAction(); // Handled in own modules
                    case 'ItxHomePage':
                    case 'ItxHomeMenPage':
                    case 'ItxHomeWomenPage':
                    case 'ItxHomeBgPage':
                        return SetHomeSeo({ index: indexHomeTemplates(seoPage.page) });
                    case 'ItxStoreLocatorPage':
                    case 'ItxAngularStoreLocatorPage':
                        return SetStoreLocatorSeo();
                    case 'ItxMyAccountPage':
                    case 'ItxAngularUserPage':
                    case 'ItxAngularMyAccountPage':
                        return SetMyAccountSeo();
                    case 'ItxCheckoutPage':
                    case 'ItxAngularCheckoutNodePage':
                    case 'ItxAngularCheckoutPage':
                        return SetCheckoutSeo();
                    case 'ItxShopCartPage':
                    case 'ItxAngularShopcartPage':
                        return SetShopCartSeo();
                    case 'ItxGiftCardPage':
                    case 'ItxAngularGiftCardPage':
                        return SetGiftCardSeo();
                    case 'Itx410ErrorPage':
                        return SetErrorPageSeo();
                    case 'ItxAngularOrderStatus':
                        return SetOrderStatus();
                    default:
                        // 'ItxAngularCmsRenderPage'
                        // 'ItxCompanyPage'
                        // 'ItxNewsletterPage'
                        // 'ItxHelpPage'
                        // 'ItxMassimoDuttiFeelPage'
                        // 'ItxChatPage'
                        // and others...
                        return SetDefaultSeo();
                }
            }),
        );
    });

    // Default route changes
    public setDefaultSeo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetDefaultSeo),
            map(() => {
                const seoTitle = 'Massimo Dutti';
                return SetHead({ title: seoTitle, metaDescription: undefined });
            }),
        );
    });

    public setHomeSeo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetHomeSeo),
            concatMap((action) =>
                this.getMenuCategories$.pipe(
                    filter(categories => categories?.length > 0),
                    take(1),
                    mergeMap((categories) => {
                        const menuCategory: CoreMenuCategory = categories[action.index];

                        if (action.index === 0) {
                            return this.translateService.get(['dev.titleseohome']).pipe(
                                take(1),
                                map((translations: Record<string, string>) => SetHead({
                                    title: `${translations['dev.titleseohome'] || 'Massimo Dutti' }`,
                                    metaDescription: menuCategory.seoCategory.metaDescription,
                                    isHome: true,
                                }))
                            );
                        }

                        return of(SetHead({
                            title: menuCategory.seoCategory.title,
                            metaDescription: menuCategory.seoCategory.metaDescription,
                            isHome: true,
                        }));
                    })
                ))
        );
    });

    public setCategorySeo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetCategorySeo),
            concatLatestFrom(() => this.store.select(getCountryCode)),
            map(([action, countryCode]) => {
                const seoCat = action.categorySeo;
                return SetHead({
                    title: `${seoCat.title} - Massimo Dutti - ${countryCode}`,
                    metaDescription: seoCat.metaDescription,
                    imgSrc: seoCat.firstProductImagePath || null,
                });
            }),
        );
    });

    public setLandingSeo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadLandingPage),
            map((action) => {
                const seoCat = action.landing.seoCategory;

                if (!(seoCat?.title && seoCat.metaDescription)) {
                    return NoOpAction();
                }

                return SetHead({
                    title: seoCat.title,
                    metaDescription: seoCat.metaDescription,
                });
            }),
        );
    });

    public setLookbookSeo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetLookbookSeo),
            map((action) => {
                const title = action.category?.seoCategory?.title;
                const seoTitle = title ? `${title} · Editorial | Massimo Dutti` : 'Massimo Dutti';
                const seoDesc = action.category?.seoCategory?.metaDescription;
                return SetHead({ title: seoTitle, metaDescription: seoDesc });
            }),
        );
    });

    // Product route changes
    public seoProductRouteChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetProductSeo),
            map((action) => {
                const seoTitle = getProductTitle(action.product);
                let seoDesc = capitalize(action.product?.name?.toLowerCase());
                // biome-ignore lint/complexity/noForEach: <explanation>
                action.product.attributes?.forEach((attr) => {
                    // Concat description attributes to build metadescription
                    if (attr.type === 'DESCRIPTION') { seoDesc += `, ${attr.value}`; }
                });
                return SetHead({ title: seoTitle, metaDescription: seoDesc, imgSrc: action.imgPath });
            }),
        );
    });

    public setStoreLocatorSeo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetStoreLocatorSeo),
            map(() => {
                const seoTitle = 'Store Locator - Massimo Dutti';
                return SetHead({ title: seoTitle, metaDescription: undefined });
            }),
        );
    });

    public setMyAccountSeo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetMyAccountSeo),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap(() => {
                return this.translateService.get(['header_my_account']).pipe(
                    filter((data) => !!data && data?.length > 0),
                    take(1),
                    map(([translations]: [any]) => {
                        const seoTitle = `${translations.header_my_account} - Massimo Dutti`;
                        return SetHead({ title: seoTitle, metaDescription: undefined });
                    })
                );
            }),
        );
    });

    public setCheckoutSeo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetCheckoutSeo),
            map(() => {
                const seoTitle = 'Checkout - Massimo Dutti';
                return SetHead({ title: seoTitle, metaDescription: undefined });
            }),
        );
    });

    public setShopcartSeo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetShopCartSeo),
            map(() => {
                const seoTitle = 'Shopcart - Massimo Dutti';
                return SetHead({ title: seoTitle, metaDescription: undefined });
            }),
        );
    });

    public setGiftCardSeo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetGiftCardSeo),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap(() => {
                return this.translateService.get(['gift-card-activation.seo.title', 'gift-card-activation.seo.metaDescription'])
                    .pipe(take(1), map((translations: string) => {
                        const seoTitle = `${translations['gift-card-activation.seo.title']} - Massimo Dutti`;
                        const seoDesc = translations['gift-card-activation.seo.metaDescription'];
                        return SetHead({ title: seoTitle, metaDescription: seoDesc });
                    }));
            }),
        );
    });

    public setOrderStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetOrderStatus),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap(() => {
                return this.translateService.get('dev.orderStatus.title1')
                    .pipe(take(1), map((translations: string) => {
                        const seoTitle = `${translations} - Massimo Dutti`;
                        return SetHead({ title: seoTitle, metaDescription: null });
                    }));
            }),
        );
    });

    public setErrorPageSeo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetErrorPageSeo),
            map(() => {
                const seoTitle = 'Error Page - Massimo Dutti';
                return SetHead({ title: seoTitle, metaDescription: undefined });
            }),
        );
    });

    public setLastSectionTranslated$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetLastSection),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap((action) => this.getCategoriesNavigation$
                .pipe(
                    filter(categories => categories?.length > 0),
                    take(1),
                    map((categories) => {
                        return { action, categories };
                    })
                )),
            filter(({ action, categories }) => action !== null && categories.length > 0),
            map(({ action, categories }) => {
                const category = categories.find(cat => cat?.sectionPathEn.toLowerCase() === action?.section?.toLowerCase());
                return SetLastSectionTranslated({ lastSectionTranslated: capitalize(category?.sectionPath) || '' });
            })
        );
    });

    // Set WW Seo
    public setWWSeo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetWorldwideSeo),
            map(() => {
                const seoTitle = 'Massimo Dutti - Official Website';
                const desc = 'Select your market and language and find the latest trends for women, men & kids on the Massimo Dutti official website. Discover elegant & exclusive styles.';
                return SetHead({ title: seoTitle, metaDescription: desc });
            }),
        );
    });

    // NOINDEX
    public setNoindexOnRouteChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetPage),
            filter(() => this.platform.isBrowser()),
            filter((action) => action.page !== 'ItxCategoryPage'),
            map((action) => {
                if (this.noindexTemplates.some((route) => action.page?.includes(route))) {
                    this.seoService.setNoIndexMeta();
                    return SetNoIndexSuccess();
                }

                return SetNoindex();
            }),
        );
    });

    public setNoindex$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetNoindex, SetCategoryNoindex),
            filter(() => this.platform.isBrowser()),
            mergeMap((action: any) => {
                return combineLatest([
                    this.store.select(getCountryCode),
                    this.store.select(getLanguageCode),
                    this.store.select(getDefaultLanguage),
                    this.store.select(getCmsConfigValue('seo_noindex')),
                ]).pipe(
                    map(([
                        countryCode,
                        languageCode,
                        defaultLang,
                        seoConfig,
                    ]: [string, string, StoreLanguage, NoIndexConfiguration]) => {
                        this.seoService.removeNoIndexMeta();

                        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
                        if (!countryCode || !languageCode || !defaultLang || !seoConfig) {
                            return SetNoIndexSuccess();
                        }

                        if (checkNoindex(seoConfig, countryCode, languageCode, defaultLang.code, action?.category)) {
                            this.seoService.setNoIndexMeta();
                        }

                        return SetNoIndexSuccess();
                    })
                );
            })
        );
    });

    // HREFLANGS
    public setCategoryHreflangs$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetCategorySeo),
            filter(() => this.platform.isBrowser()),
            concatLatestFrom(() => this.routeTypeService.isDirectLoad$),
            filter(([_, isDirectLoad]) => !isDirectLoad),
            mergeMap(([cat]) =>
                combineLatest([
                    this.store.select(getStoreList),
                    this.store.select(getStoreId),
                    this.store.select(getCmsConfigValue('seo_noindex')),
                    this.store.select(getCountryCode),
                    this.store.select(getLanguageCode),
                    this.store.select(getDefaultLanguage),
                ]).pipe(
                    mergeMap(([storeList, storeId, indexConfs, countryCode, languageCode, defaultLang]) => {
                        if (checkNoindex(indexConfs, countryCode, languageCode, defaultLang.code, cat?.category)) {
                            this.seoService.removeHreflangs();
                            return of(NoOpAction());
                        }

                        const categoryId = cat?.category?.id;
                        if (!(categoryId && storeList?.length && storeId)) {
                            return of(NoOpAction());
                        }

                        return this.seoApiRepository.getCategoryTranslations(categoryId, storeId).pipe(
                            map((translations) => {
                                this.seoService.regenerateHreflangs(storeList, indexConfs, {
                                    translations: translations?.categoryUrlTranslations,
                                    isCategory: true,
                                    categoryAttachments: cat.category?.attachments,
                                });
                                return NoOpAction();
                            }),
                        );
                    })
                ))
        );
    });

    public setProductHreflangs$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetProductSeo),
            filter(() => this.platform.isBrowser()),
            concatLatestFrom(() => this.routeTypeService.isDirectLoad$),
            filter(([_, isDirectLoad]) => !isDirectLoad),
            mergeMap(([action]) =>
                combineLatest([
                    this.store.select(getStoreList),
                    this.store.select(getCmsConfigValue('seo_noindex')),
                    this.store.select(getCountryCode),
                    this.store.select(getLanguageCode),
                    this.store.select(getDefaultLanguage),
                ]).pipe(
                    map(([storeList, indexConfs, countryCode, languageCode, defaultLang]) => {
                        if (checkNoindex(indexConfs, countryCode, languageCode, defaultLang.code)) {
                            this.seoService.removeHreflangs();
                            return NoOpAction();
                        }

                        const productTranslations = action.product.productUrlTranslations;
                        this.seoService.regenerateHreflangs(storeList, indexConfs, {
                            translations: productTranslations,
                        });
                        return NoOpAction();
                    })
                ))
        );
    });

    public setHreflangsOnRouteChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetPage),
            filter((action) => action.page !== 'ItxCategoryPage' && action.page !== 'ItxProductPage'),
            concatLatestFrom(() => this.routeTypeService.isDirectLoad$),
            filter(([_, isDirectLoad]) => {
                return !isDirectLoad;
            }),
            mergeMap(([action]) =>
                combineLatest([
                    this.store.select(getStoreList),
                    this.store.select(getCmsConfigValue('seo_noindex')),
                    this.store.select(routerPublicQueries.getUrlFromRouter),
                    this.store.select(getStoreAndLanguage),
                    this.store.select(getCountryCode),
                    this.store.select(getLanguageCode),
                    this.store.select(getDefaultLanguage),
                ]).pipe(
                    map(([storeList, indexConfs, url, storeLang, countryCode, languageCode, defaultLang]) => {
                        const isNoindex = this.noindexTemplates.some((route) => action.page?.includes(route))
                            || checkNoindex(indexConfs, countryCode, languageCode, defaultLang.code);
                        if (isNoindex) {
                            this.seoService.removeHreflangs();
                            return NoOpAction();
                        }

                        const sanitizedUrl = removeCountryAndLanguageFromUrl(
                            url,
                            storeLang.countryCode.toLowerCase(),
                            storeLang.languageCode.toLowerCase()
                        );
                        this.seoService.regenerateHreflangs(storeList, indexConfs, {
                            urlPath: sanitizedUrl,
                        });
                        return NoOpAction();
                    })
                ))
        );
    });

    public setSchemaOnDefaultRouteChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetPage),
            concatLatestFrom(() => this.routeTypeService.isDirectLoad$),
            filter(([_, isDirectLoad]) => !isDirectLoad),
            map(([action]) => {
                switch (action.page) {
                    case 'ItxWorldWidePage':
                    case 'ItxHomePage':
                        this.seoService.setOrganizationSchema();
                        break;
                    case 'ItxProductPage':
                        break;
                    default:
                        this.seoService.removeOrganizationSchema();
                        this.seoService.removeProductSchema();
                }

                return NoOpAction();
            }),
        );
    });

    public setProductSchema$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetProductSeo),
            filter(() => this.platform.isBrowser()),
            concatLatestFrom(() => this.routeTypeService.isDirectLoad$),
            filter(([_, isDirectLoad]) => !isDirectLoad),
            map(([action]) => {
                const countryCode = this.store.selectSignal(getCountryCode)();
                const currency = this.store.selectSignal(getCurrencyInfo)().currencyCode;
                const url: string = this.window.location.href;
                let seoDesc = capitalize(action.product?.name?.toLowerCase());
                // biome-ignore lint/complexity/noForEach: <explanation>
                action.product.attributes?.forEach((attr) => {
                    if (attr.type === 'DESCRIPTION') { seoDesc += `, ${attr.value}`; }
                });

                this.seoService.setProductSchema(action.product, countryCode, currency, seoDesc, url);
                return NoOpAction();
            }),
        );
    });
}
